/**
 * A math class that parametrizes a world space position based on a cartographic latitude and longitude combined with
 * a height above the ellipsoid surface.
 *
 * The position is described as a combination of the position on the surface of an ellipsoid + the height above that
 * point along the surface normal at that point.
 *
 * A height value of 0 simply decays the class to a latitude/longitude cartographic position.
 */
export class Cartographic {
    /**
     * Constructs a new Cartographic coordinate.
     *
     * @param longitude Longitude, defaults to 0
     * @param latitude Latitude, defaults to 0
     * @param height Height, defaults to 0
     */
    constructor(longitude = 0, latitude = 0, height = 0) {
        /**
         * Longitude in radians.
         */
        this.longitude = 0;
        /**
         * Latitude in radians.
         */
        this.latitude = 0;
        /**
         * Height above the surface in meters.
         */
        this.height = 0;
        this.longitude = longitude;
        this.latitude = latitude;
        this.height = height;
    }
    /**
     * Sets all the fields of the cartographic position and returns a reference to this.
     *
     * @param longitude
     * @param latitude
     * @param height
     */
    set(longitude = 0, latitude = 0, height = 0) {
        this.longitude = longitude;
        this.latitude = latitude;
        this.height = height;
        return this;
    }
    /**
     * Sets the longitude of this to the provided value, returning this for chaining.
     *
     * @param longitude Longitude in radians
     * @returns
     */
    setLongitude(longitude) {
        this.longitude = longitude;
        return this;
    }
    /**
     * Sets the latitude of this to the provided value, returning this for chaining.
     *
     * @param latitude Geodetic latitude in radians
     * @returns
     */
    setLatitude(latitude) {
        this.latitude = latitude;
        return this;
    }
    /**
     * Sets the height of this to the provided value, returning this for chaining.
     *
     * @param height Height in meters
     * @returns
     */
    setHeight(height) {
        this.height = height;
        return this;
    }
    /**
     * Returns true if 'this' position exactly equals the 'other' position. This uses direct floating point equals
     * comparison without any epsilon.
     *
     * @param other
     * @returns
     */
    equals(other) {
        return (this.longitude === other.longitude &&
            this.latitude === other.latitude &&
            this.height === other.height);
    }
    /**
     * Returns true if 'this' position exactly equals the 'other' position, within the given range 'epsilon'.
     *
     * Sometimes an 'epsilon' is needed in floating point comparisons as numerical error and limited precision means
     * that some operations will produce slightly incorrect results. e.g. Using strict equality for '1.1 - 0.9 == 0.2'
     * will return false under IEE754 FP64. An epsilon adds an 'allowed error' to catch these kinds of errors.
     *
     * @param other
     * @param epsilon
     * @returns
     */
    equalsEpsilon(other, epsilon) {
        // Simple epsilon is fine here as Rectangle will only use small floats (-Math.PI >= v >= Math.PI)
        return (Math.abs(this.longitude - other.longitude) <= epsilon &&
            Math.abs(this.latitude - other.latitude) <= epsilon &&
            Math.abs(this.height - other.height) <= epsilon);
    }
    /**
     * Get a new Cartographic instance with longitude, latitude and height copied from this.
     *
     * @returns
     */
    clone() {
        return new Cartographic().copy(this);
    }
    /**
     * Copies the longitude, latitude and height from the other Cartographic into this.
     *
     * Returns a reference to this for chaining.
     *
     * @param other
     * @returns
     */
    copy(other) {
        return this.set(other.longitude, other.latitude, other.height);
    }
}
